import { DndContext, DragOverlay } from '@dnd-kit/core';
import { restrictToFirstScrollableAncestor, restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import React, { useEffect, useState } from 'react';
import { IFile } from '../Controls/Upload/types';
import ItemImage from './components/ItemImage/ItemImage';
import ItemImageUpload from './components/ItemImageUpload/ItemImageUpload';
import ItemImagesHeader from './containers/ItemImagesHeader/ItemImagesHeader';
import ItemImagesList from './containers/ItemImagesList/ItemImagesList';
import './style.scss';
import { IItemImagesProps } from './types';
import { isDisabledByPos } from 'src/utils/helpers';
import { DndSensors, dndAutoScrollOptionsDefault, dndOverlayAnimationDefault } from 'src/utils/dnd';

const ItemImages: React.FC<IItemImagesProps> = (props: IItemImagesProps): JSX.Element => {
  const dndSensors = DndSensors();
  // full width add/format image mode
  const [addMode, setAddMode] = useState<boolean>(false);
  // delete/sort images mode. Image array state draft mode
  const [editMode, setEditMode] = useState<boolean>(false);
  const [imageValid, setImageValid] = useState<boolean>(props.validation);

  const imagesRestrictionAccess = props.posRestrictions;
  const posSetting: boolean = isDisabledByPos(imagesRestrictionAccess);

  useEffect(() => {
    if (imageValid && !props.validation) setImageValid(false);
  }, [props.validation]);

  const uploadImage = (_image: string) => {
    props.uploadImage(_image);
    if (addMode) setAddMode(false);
    if (!editMode) setEditMode(true);
    // update validation
    if (!props.validation) props.setValidation();
  };

  const onChangeAddMode = (file: IFile) => {
    setAddMode(!file.error && !file.result ? false : true);
    setImageValid(Boolean(file.result));
  };

  const changeEditMode = () => {
    if (disabled) return;

    if (editMode) imagesChangesSubmit();
    else setEditMode(true);
  };

  const imagesChangesSubmit = (reset?: boolean) => {
    props.imagesChangesSubmit(reset);
    setEditMode(false);
  };

  const disabled: boolean = Boolean(posSetting || props.disabled);

  return (
    <div className={`items-images__container pb-2 ${props.images?.length && !addMode ? '' : 'items-images__container_none-border'}`}>
      {/* header with requirements and control block */}
      <ItemImagesHeader
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
        disabled={disabled}
        addMode={addMode}
        editMode={editMode}
        imageValid={imageValid}
        imagesRestrictionAccess={imagesRestrictionAccess}
        onChangeEditMode={changeEditMode}
        onImagesChangesSubmit={() => imagesChangesSubmit(true)}
      />
      {/* images block */}
      {props.images?.length ? (
        <DndContext
          id="item-images"
          autoScroll={dndAutoScrollOptionsDefault}
          sensors={dndSensors}
          onDragStart={({ active }) => props.setDraggableImage(active.id)}
          onDragCancel={() => props.setDraggableImage(null)}
          onDragEnd={props.handleDragEnd}
        >
          <ItemImagesList
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
            disabled={disabled}
            addMode={addMode}
            editMode={editMode}
            imageValid={imageValid}
            imagesRestrictionAccess={imagesRestrictionAccess}
            onChangeAddMode={onChangeAddMode}
            onChangeEditMode={changeEditMode}
            onUploadImage={uploadImage}
          />
          {/* drag overlay*/}
          <DragOverlay
            dropAnimation={dndOverlayAnimationDefault}
            modifiers={[restrictToHorizontalAxis, restrictToFirstScrollableAncestor]}
            wrapperElement="ul"
          >
            {props.draggableImage ? (
              <ItemImage
                key="image-overlay"
                id="image-overlay"
                image={props.draggableImage}
                imagesLength={props.images?.length}
                index="items-image-overlay"
                editMode={editMode}
                posRestrictions={imagesRestrictionAccess}
                changeEditMode={() => setEditMode(true)}
                deleteImage={props.deleteImage}
                className="item-image__drag-active"
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      ) : (
        // full width image add image button(drag zone)
        <ItemImageUpload
          image=""
          onChange={onChangeAddMode}
          uploadImage={uploadImage}
          validation={imageValid}
          required={!imageValid}
          disabled={disabled}
          className="full-width"
        />
      )}
    </div>
  );
};

export default ItemImages;
